/**
 * Fill in the blanks editor
 */

import * as React from 'react';
import {
    cn,
    Label,
    Input,
    Textarea,
    RichTextEditor,
    Switch,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Badge,
} from '@ai-content-hub/content-kit';
import { EditViewToggle, type EditTabDescriptor } from '@ai-content-hub/content-core';
import type { ContentConfig } from '@ai-content-hub/content-core';
import { FileEdit, Settings } from 'lucide-react';
import type { FITBElementData, FITBConfig, FITBBlankMode, FITBBlank } from './schema';
import {
    defaultFITBConfig,
    defaultFITBElementData,
    fitbElementSchema,
    getFITBBlankMode,
    normalizeFITBData,
    countBlanksInTiptap,
} from './schema';
import filter from 'lodash/filter';
import map from 'lodash/map';
import trim from 'lodash/trim';
import {
    FITB_VALIDATE_ACCEPTED_ANSWER_REQUIRED,
    FITB_VALIDATE_PASSAGE_REQUIRED,
} from './grader';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export interface FITBEditProps {
    value: FITBElementData;
    onChange: (value: FITBElementData) => void;
    config?: ContentConfig;
    onConfigChange?: (config: ContentConfig) => void;
    disabled?: boolean;
    errors?: Record<string, string>;
    configSlot?: React.ReactNode;
    editorOptions?: { showTitle?: boolean; showDescription?: boolean };
    className?: string;
    language?: string;
}

/** Multiple accepted answers / distractors: use semicolons (commas are allowed inside an answer). */
function parseListInput(raw: string): string[] {
    return raw.split(/[;\r\n]+/);
}

/** Final trim: used when committing (on blur) to clean up whitespace. */
function parseListInputFinal(raw: string): string[] {
    return map(raw.split(/[;\r\n]+/), (s) => trim(s));
}

function listToInput(list: string[] | undefined): string {
    return (list ?? []).join('; ');
}

/* ------------------------------------------------------------------ */
/* Semicolon-list input with local state (spaces preserved while typing) */
/* ------------------------------------------------------------------ */

interface BlankListInputProps {
    id?: string;
    value: string[] | undefined;
    onChange: (list: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
}

/**
 * A controlled input that stores a semicolon-separated list.
 * Keeps a local draft while the user types so spaces are not stripped
 * mid-keystroke. On blur the draft is trimmed and committed.
 */
const BlankListInput: React.FC<BlankListInputProps> = ({
    id,
    value,
    onChange,
    placeholder,
    disabled,
    className,
    ...ariaProps
}) => {
    const externalText = listToInput(value);
    const [draft, setDraft] = React.useState(externalText);
    const [focused, setFocused] = React.useState(false);

    // Sync draft from external value when not focused
    React.useEffect(() => {
        if (!focused) {
            setDraft(externalText);
        }
    }, [externalText, focused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setDraft(raw);
        // Live update with untrimmed parse so parent state stays in sync
        onChange(parseListInput(raw));
    };

    const handleBlur = () => {
        setFocused(false);
        // Final commit: trim all entries
        const cleaned = parseListInputFinal(draft);
        onChange(cleaned);
        setDraft(listToInput(cleaned));
    };

    return (
        <Input
            id={id}
            value={draft}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            aria-invalid={ariaProps['aria-invalid']}
            aria-describedby={ariaProps['aria-describedby']}
        />
    );
};

export const FITBEdit: React.FC<FITBEditProps> = ({
    value,
    onChange,
    config,
    onConfigChange,
    disabled = false,
    errors = {},
    configSlot,
    className,
    language,
}) => {
    const { t } = useElementSchemaLocale(fitbElementSchema.i18n, { config, language });

    const [activeTab, setActiveTab] = React.useState<'content' | 'config'>('content');

    const mergedConfig = React.useMemo((): FITBConfig => {
        return { ...defaultFITBConfig, ...(config ?? {}) };
    }, [config]);

    const blankMode = getFITBBlankMode(mergedConfig);

    const safeData = React.useMemo(
        () => normalizeFITBData({ ...defaultFITBElementData, ...value }),
        [value]
    );

    const updateData = (patch: Partial<FITBElementData>) => {
        onChange(normalizeFITBData({ ...safeData, ...patch }));
    };

    const handlePassageChange = (passage: FITBElementData['passage']) => {
        updateData({ passage });
    };

    const handleConfigPatch = (patch: Partial<FITBConfig>) => {
        onConfigChange?.({ ...mergedConfig, ...patch });
    };

    const updateBlank = (index: number, patch: Partial<FITBBlank>) => {
        const blanks = map(safeData.blanks, (b, i) => (i === index ? { ...b, ...patch } : b));
        onChange({ ...safeData, blanks });
    };

    const blankModeOptions = React.useMemo(
        () =>
            [
                { value: 'type' as const, label: t('blankModeType') },
                { value: 'dropdown' as const, label: t('blankModeDropdown') },
                { value: 'drag' as const, label: t('blankModeDrag') },
            ],
        [t],
    );

    const tabs: EditTabDescriptor[] = React.useMemo(
        () => [
            { id: 'content', label: t('content'), icon: FileEdit },
            ...(configSlot ? [{ id: 'config', label: t('configuration'), icon: Settings }] : []),
        ],
        [t, configSlot],
    );

    const blankCount = countBlanksInTiptap(safeData.passage);
    const passageError =
        errors.passage ?? (blankCount === 0 ? FITB_VALIDATE_PASSAGE_REQUIRED : undefined);

    return (
        <div className={cn('fitb-edit flex flex-col gap-4', className)}>
            {tabs.length > 1 ? (
                <EditViewToggle
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={(id) => setActiveTab(id as 'content' | 'config')}
                    className="fitb-edit-view-toggle"
                    buttonClassPrefix="fitb-edit-view-toggle-button"
                />
            ) : null}

            {activeTab === 'config' && configSlot ? (
                <div className="fitb-edit-config space-y-6 overflow-y-auto">
                    <div className="fitb-edit-config-fitb rounded-xl border-0 bg-card p-4 shadow-sm space-y-4">
                        <h3 className="fitb-edit-config-fitb-title text-lg font-semibold">
                            {t('fillInTheBlanks')}
                        </h3>
                        <div className="space-y-2">
                            <Label>{t('answerMode')}</Label>
                            <Select
                                value={blankMode}
                                onValueChange={(v) =>
                                    handleConfigPatch({ blankMode: v as FITBBlankMode })
                                }
                                disabled={disabled}
                            >
                                <SelectTrigger className="fitb-edit-config-mode-trigger w-full max-w-md">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {map(blankModeOptions, (o) => (
                                        <SelectItem key={o.value} value={o.value}>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center justify-between gap-4 rounded-sm bg-muted/30 p-3 border border-border">
                            <div>
                                <Label htmlFor="fitb-shuffle" className="text-base">
                                    {t('shuffleOptionsFITB')}
                                </Label>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t('shuffleFITBDesc')}
                                </p>
                            </div>
                            <Switch
                                id="fitb-shuffle"
                                checked={mergedConfig.shuffleOptions !== false}
                                onCheckedChange={(c) => handleConfigPatch({ shuffleOptions: c })}
                                disabled={disabled}
                            />
                        </div>
                        {blankMode === 'type' ? (
                            <div className="flex items-center justify-between gap-4 rounded-sm bg-muted/30 p-3 border border-border">
                                <div>
                                    <Label htmlFor="fitb-case" className="text-base">
                                        {t('caseSensitiveFITB')}
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {t('caseSensitiveDesc')}
                                    </p>
                                </div>
                                <Switch
                                    id="fitb-case"
                                    checked={Boolean(mergedConfig.caseSensitive)}
                                    onCheckedChange={(c) => {
                                        handleConfigPatch({ caseSensitive: c });
                                        onChange({
                                            ...safeData,
                                            gradingCaseSensitive: c,
                                        });
                                    }}
                                    disabled={disabled}
                                />
                            </div>
                        ) : null}
                    </div>
                    <div className="fitb-edit-config-slot">{configSlot}</div>
                </div>
            ) : (
                <div className="fitb-edit-content space-y-6 overflow-y-auto">
                    <div className="fitb-edit-passage rounded-xl border-0 bg-card p-4 shadow-sm space-y-2">
                        {/* EAA-2066: passage (>= 1 blank) and each blank's accepted answer are
                            the fields fitbGrader.validate requires — show the "*" marker. */}
                        <Label className="text-base font-semibold">
                            {t('passageLabelFITB')} <span className="text-destructive">*</span>
                        </Label>
                        <p className="text-sm text-muted-foreground">{t('passageFITBHint')}</p>
                        <RichTextEditor
                            value={safeData.passage}
                            onChange={handlePassageChange}
                            readOnly={disabled}
                            minHeight={220}
                            className="fitb-edit-passage-editor"
                            error={Boolean(passageError)}
                            translate={t}
                        />
                        {passageError ? (
                            <p
                                id="fitb-edit-passage-error"
                                role="alert"
                                className="fitb-edit-passage-error text-sm text-destructive mt-1"
                            >
                                {passageError}
                            </p>
                        ) : null}
                    </div>

                    <div className="fitb-edit-blanks space-y-3">
                        <h3 className="text-lg font-semibold">{t('blanksHeading')}</h3>
                        {errors.blanks ? (
                            <p
                                role="alert"
                                className="fitb-edit-blanks-error text-sm text-destructive"
                            >
                                {errors.blanks}
                            </p>
                        ) : null}
                        {safeData.blanks.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">
                                {t('blanksEmptyFITB')}
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {map(safeData.blanks, (blank, index) => {
                                    const acceptedFieldKey = `blanks.${index}.acceptedAnswers`;
                                    const hasAcceptedAnswer =
                                        filter(blank.acceptedAnswers, (a) => trim(String(a)) !== '')
                                            .length > 0;
                                    const acceptedError =
                                        errors[acceptedFieldKey] ??
                                        (blankCount > 0 && !hasAcceptedAnswer
                                            ? FITB_VALIDATE_ACCEPTED_ANSWER_REQUIRED
                                            : undefined);
                                    return (
                                        <div
                                            key={blank.id}
                                            className="fitb-edit-blank-row rounded-sm bg-muted/30 p-3 border border-border space-y-3"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant="secondary"
                                                    className="fitb-edit-blank-badge w-9 justify-center bg-info/10 text-info"
                                                >
                                                    {index + 1}
                                                </Badge>
                                                <span className="text-sm text-muted-foreground">
                                                    {t('blankNth')} {index + 1}
                                                </span>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor={`fitb-accept-${index}`}>
                                                    {t('acceptedAnswers')}{' '}
                                                    <span className="text-destructive">*</span>
                                                </Label>
                                                <BlankListInput
                                                    id={`fitb-accept-${index}`}
                                                    value={blank.acceptedAnswers}
                                                    onChange={(list) =>
                                                        updateBlank(index, {
                                                            acceptedAnswers: list,
                                                        })
                                                    }
                                                    placeholder={t('correctAnswersSemicolonHint')}
                                                    disabled={disabled}
                                                    className={cn(
                                                        acceptedError &&
                                                            'border-destructive ring-1 ring-destructive/40'
                                                    )}
                                                    aria-invalid={acceptedError ? true : undefined}
                                                    aria-describedby={
                                                        acceptedError
                                                            ? `fitb-edit-blank-${index}-accepted-error`
                                                            : undefined
                                                    }
                                                />
                                                {acceptedError ? (
                                                    <p
                                                        id={`fitb-edit-blank-${index}-accepted-error`}
                                                        role="alert"
                                                        className="fitb-edit-blank-accepted-error text-sm text-destructive"
                                                    >
                                                        {acceptedError}
                                                    </p>
                                                ) : null}
                                            </div>
                                            {(blankMode === 'drag' || blankMode === 'dropdown') && (
                                                <div className="space-y-2">
                                                    <Label htmlFor={`fitb-dist-${index}`}>
                                                        {t('wrongOptionsFITB')}
                                                    </Label>
                                                    <BlankListInput
                                                        id={`fitb-dist-${index}`}
                                                        value={blank.distractors}
                                                        onChange={(list) =>
                                                            updateBlank(index, {
                                                                distractors: list,
                                                            })
                                                        }
                                                        placeholder={t('wrongOptionsSemicolonHint')}
                                                        disabled={disabled}
                                                    />
                                                </div>
                                            )}
                                            {/* <div className="space-y-2">
                                            <Label htmlFor={`fitb-ph-${index}`}>
                                                Placeholder (optional)
                                            </Label>
                                            <Input
                                                id={`fitb-ph-${index}`}
                                                value={blank.placeholder ?? ''}
                                                onChange={(e) =>
                                                    updateBlank(index, {
                                                        placeholder: e.target.value,
                                                    })
                                                }
                                                disabled={disabled}
                                            />
                                        </div> */}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="fitb-edit-explanation space-y-2">
                        <Label htmlFor="fitb-explanation">{t('explanationAfterSubmit')}</Label>
                        <Textarea
                            id="fitb-explanation"
                            value={safeData.explanation ?? ''}
                            onChange={(e) => updateData({ explanation: e.target.value })}
                            placeholder={t('explainCorrectAnswer')}
                            rows={3}
                            disabled={disabled}
                            className="fitb-edit-explanation-input"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
